import React from 'react';
import { X } from 'lucide-react';

export interface UnternehmerFestsetzungHistorieEntry {
    monat: string;
    lockedAt?: string;
    lockedBy?: string;
    snapshot?: {
        arbeitstage?: number;
        wochenstunden?: number;
        stdLohn?: number;
        planumsatz?: number;
        sollumsatzPStd?: number;
    };
}

interface FestsetzungsHistorieModalUnternehmerProps {
    isOpen: boolean;
    onClose: () => void;
    currencyCode: string;
    unternehmerName: string;
    entries: UnternehmerFestsetzungHistorieEntry[];
    halbjahrLabel?: string;
}

const formatCurrency = (value: number | undefined, currencyCode: string, decimalPlaces = 0): string => {
    const num = Number(value) || 0;
    return `${new Intl.NumberFormat('de-DE', {
        style: 'decimal',
        minimumFractionDigits: decimalPlaces,
        maximumFractionDigits: decimalPlaces,
    }).format(num)} ${currencyCode}`;
};

const renderMetricCell = (label: string, value: React.ReactNode, valueClass = 'text-white') => (
    <div>
        <div className="text-slate-500 text-xs uppercase mb-1">{label}</div>
        <div className={`font-mono font-bold text-sm ${valueClass}`}>{value}</div>
    </div>
);

const renderEntry = (entry: UnternehmerFestsetzungHistorieEntry, currencyCode: string) => {
    const snap = entry.snapshot || {};

    return (
        <div
            key={entry.monat}
            className="border border-slate-700 rounded-lg p-4 bg-slate-800/50"
        >
            <div className="flex flex-wrap items-center justify-between gap-2 mb-3 border-b border-slate-700 pb-2">
                <span className="text-white font-bold">{entry.monat}</span>
                {(entry.lockedAt || entry.lockedBy) && (
                    <div className="text-xs text-slate-500">
                        {entry.lockedAt && <span>{entry.lockedAt}</span>}
                        {entry.lockedBy && <span>{entry.lockedAt ? ' · ' : ''}{entry.lockedBy}</span>}
                    </div>
                )}
            </div>
            {entry.snapshot ? (
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 text-sm">
                    {renderMetricCell('Arbeitstage', `${snap.arbeitstage ?? 0} Tage`)}
                    {renderMetricCell('Wochenstd.', `${(snap.wochenstunden ?? 0).toLocaleString('de-DE', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} Std`)}
                    {renderMetricCell('Std. Lohn', formatCurrency(snap.stdLohn, currencyCode, 2))}
                    {renderMetricCell('Planumsatz', formatCurrency(snap.planumsatz, currencyCode), 'text-[#E6C299]')}
                    {renderMetricCell('Soll p. Std', formatCurrency(snap.sollumsatzPStd, currencyCode, 0), 'text-orange-400')}
                </div>
            ) : (
                <p className="text-slate-500 text-sm italic">Kein Monats-Snapshot vorhanden (Legacy-Daten).</p>
            )}
        </div>
    );
};

const FestsetzungsHistorieModalUnternehmer: React.FC<FestsetzungsHistorieModalUnternehmerProps> = ({
    isOpen,
    onClose,
    currencyCode,
    unternehmerName,
    entries,
    halbjahrLabel,
}) => {
    if (!isOpen) {
        return null;
    }

    return (
        <div
            className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={onClose}
        >
            <div
                className="bg-slate-900 border border-slate-700 rounded-xl shadow-2xl w-full max-w-4xl max-h-[85vh] flex flex-col"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-center justify-between px-6 py-4 border-b border-slate-700 shrink-0">
                    <div>
                        <h2 className="text-lg font-bold text-white tracking-wide">Festsetzungs-Historie</h2>
                        <p className="text-sm text-slate-400 mt-0.5">
                            {unternehmerName}
                            {halbjahrLabel ? ` · ${halbjahrLabel}` : ''}
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="text-slate-400 hover:text-white bg-slate-800 hover:bg-slate-700 rounded-lg p-2 transition-colors"
                        aria-label="Schließen"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="overflow-y-auto px-6 py-5 space-y-3">
                    {entries.length > 0 ? (
                        entries.map((entry) => renderEntry(entry, currencyCode))
                    ) : (
                        <p className="text-slate-500 text-sm italic">Keine Festsetzungen vorhanden.</p>
                    )}
                </div>
            </div>
        </div>
    );
};

export default FestsetzungsHistorieModalUnternehmer;
